/* eslint-disable arrow-body-style */
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import
{
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc,
  DocumentData,
} from 'firebase/firestore';
import { Invitation, UserDb } from 'src/guards/admin/types';
import { DB } from 'src/contexts/FirebaseContext';

export interface UserCompleteRequest
{
  uid: string;
  firstName: string;
  lastName: string;
  password: string;
}

export interface TicketBasicRequest
{
  id: string;
  startDate?: Date;
  endDate?: Date;
}

export interface IdsData
{
  agentUserIds: string[];
  agentsIds: { [key: string]: boolean };
  userIds: string[];
  allIds: string[];
}

// Define a service using a base URL and expected endpoints
export const AdminApi = createApi({
  reducerPath: 'AdminApi',
  baseQuery: fakeBaseQuery(),
  refetchOnReconnect: true,
  tagTypes: ['User', 'Invitation'],
  endpoints: (builder) => ({
    //users
    fetchInvitationById: builder.query<Invitation, string>({
      async queryFn(invitationId: string)
      {
        try {
          if (!invitationId) {
            return { error: { message: 'Invitation ID is required' } };
          }

          console.log('Fetching invitation:', invitationId);
          const invitationRef = doc(DB, 'invitations', invitationId);
          const invitationSnap = await getDoc(invitationRef);

          if (!invitationSnap.exists()) {
            console.log('No invitation found with ID:', invitationId);
            return { error: { message: 'Invitation not found' } };
          }

          const invitationData = invitationSnap.data() as Invitation;
          console.log('Fetched invitation data:', invitationData);
          return { data: invitationData };
        } catch (error) {
          console.error('Error fetching invitation:', error);
          return {
            error: {
              message: error.message,
              code: error.code,
              status: error.name === 'FirebaseError' ? 'FIREBASE_ERROR' : 'UNKNOWN_ERROR'
            }
          };
        }
      },
      providesTags: (result, error, invitationId) =>
        result
          ? [{ type: 'Invitation', id: result?.invitationId }]
          : [{ type: 'Invitation', id: 'SINGLE' }],
    }),
    fetchInvitationsByInviterId: builder.query<Invitation[], string>({
      async queryFn(inviterId: string)
      {
        try
        {
          const invitationsRef = collection(DB, 'invitations');
          const invitationsSnapshot = await getDocs(
            query(invitationsRef, where('inviterId', '==', inviterId))
          );
          const invitations: Invitation[] = [];
          invitationsSnapshot.forEach((doc) =>
          {
            invitations.push({ ...doc.data(), invitationId: doc.id } as Invitation);
          });
          return { data: invitations };
        } catch (error)
        {
          return { error: { status: 500, data: 'Error fetching invitations' } };
        }
      },
      providesTags: (result) =>
        result
          ? result.map(({ invitationId }) => ({ type: 'Invitation', id: invitationId }))
          : [{ type: 'Invitation', id: 'LIST' }],
    }),
    fetchUsers: builder.query<UserDb[], { authId: string; orgId: string }>({
      async queryFn({ authId, orgId })
      {
        console.log('fetchUsers', authId, orgId);
        const constraints = [];
        constraints.push(where('ownerId', '==', authId));
        constraints.push(where('orgId', '==', orgId));
        let usersRef = collection(DB, 'users');
        const qUser = query(usersRef, ...constraints);
        let usersData: UserDb[] = (await getDocs(qUser)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }));
        return { data: usersData };
      },
      providesTags: (result, error, authId) =>
        result
          ? [
            ...result.map(({ uid }) => ({ type: 'User' as const, uid })),
            { type: 'User', id: 'LIST' },
          ]
          : [{ type: 'User', id: 'LIST' }],
    }),
    fetchUserIds: builder.query<string[], { authId: string; orgId: string }>({
      queryFn: async ({ authId, orgId }) =>
      {
        const userConstraints = [];
        userConstraints.push(where('ownerId', '==', authId));
        userConstraints.push(where('orgId', '==', orgId));
        let usersRef = collection(DB, 'users');
        const qUser = query(usersRef, ...userConstraints);
        let userIds: string[] = (await getDocs(qUser)).docs.map(({ id }) => id);
        return { data: userIds };
      },
      providesTags: (result, error, authId) =>
        result
          ? [...result.map((uid) => ({ type: 'User' as const, uid })), { type: 'User', id: 'LIST' }]
          : [{ type: 'User', id: 'LIST' }],
    }),
    fetchAgents: builder.query<UserDb[], { orgId: string }>({
      async queryFn({ orgId })
      {
        console.log('fetchAgents', orgId);
        // Fetch the organisation document
        const orgRef = doc(DB, 'organisers', orgId);
        const orgDoc = await getDoc(orgRef);
        if (!orgDoc.exists())
        {
          console.log(`No organisation found with id: ${orgId}`);
          return { data: [] };
        }
        // Extract the agentIds from the organisation document
        const { agentsIds } = orgDoc.data();
        console.log('agentsIds', agentsIds);
        const constraints = [];
        constraints.push(where('uid', 'in', Object.keys(agentsIds)));
        let usersRef = collection(DB, 'users');
        const qUser = query(usersRef, ...constraints);
        let agentsData: UserDb[] = (await getDocs(qUser)).docs.map((doc: DocumentData) =>
        {
          const isPending = !agentsIds[doc.id]; // directly assign the value
          return {
            id: doc.id,
            isPending,
            ...doc.data(),
          };
        });
        return { data: agentsData };
      },
      providesTags: (result, error, orgId) =>
        result
          ? [
            ...result.map(({ uid }) => ({ type: 'User' as const, uid })),
            { type: 'User', id: 'LIST' },
          ]
          : [{ type: 'User', id: 'LIST' }],
    }),
    fetchIdsAndPendings: builder.query<IdsData, { authId: string; orgId: string }>({
      async queryFn({ authId, orgId })
      {
        // UserIds
        const userConstraints = [];
        userConstraints.push(where('ownerId', '==', authId));
        userConstraints.push(where('orgId', '==', orgId));
        let usersRef = collection(DB, 'users');
        const qUser = query(usersRef, ...userConstraints);
        let userIds: string[] = (await getDocs(qUser)).docs.map(({ id }) => id);
        // AgentIds
        const orgRef = doc(DB, 'organisers', orgId);
        const orgDoc = await getDoc(orgRef);
        if (!orgDoc.exists())
        {
          console.log(`No organisation found with id: ${orgId}`);
          return { data: { agentUserIds: [], agentsIds: {}, userIds, allIds: userIds } };
        }
        // Extract the agentIds from the organisation document
        const { agentsIds } = orgDoc.data();
        console.log('agentsIds', agentsIds);
        let agentUserIds: string[] = Object.keys(agentsIds);
        // Combine the two
        let idsData: IdsData = {
          agentUserIds,
          agentsIds,
          userIds,
          allIds: [...userIds, ...agentUserIds],
        };
        return {
          data: idsData,
        };
      },
      providesTags: (result, error, orgId) =>
        result
          ? [
            ...result.allIds.map((uid) => ({ type: 'User' as const, uid })),
            { type: 'User', id: 'LIST' },
          ]
          : [{ type: 'User', id: 'LIST' }],
    }),
    fetchUser: builder.query<UserDb, string>({
      async queryFn(userId)
      {
        let userDocRef = doc(DB, 'users', userId);
        let userData = (await getDoc(userDocRef)).data() as UserDb;
        return { data: userData };
      },
      providesTags: (result, error, userId) => [{ type: 'User', id: userId }],
    }),
    fetchUserByUid: builder.query<UserDb, string>({
      async queryFn(uid)
      {
        let userDocRef = doc(DB, 'users', uid);
        let userData = (await getDoc(userDocRef)).data() as UserDb;
        return { data: userData };
      },
      providesTags: (result, error, uid) => [{ type: 'User', id: uid }],
    }),
    fetchUserByEmail: builder.query<UserDb, string>({
      async queryFn(email)
      {
        let userDocRef = doc(DB, 'users', email);
        let userData = (await getDoc(userDocRef)).data() as UserDb;
        return { data: userData };
      },
      providesTags: (result, error, email) => [{ type: 'User', id: email }],
    }),
    // user mutations
    editUserStatus: builder.mutation<boolean, string>({
      async queryFn(userid)
      {
        let userDocRef = doc(DB, 'users', userid);
        updateDoc(userDocRef, {
          isNewUser: false,
        });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'User', id: arg }],
    }),
    editUser: builder.mutation<boolean, UserDb>({
      async queryFn(user)
      {
        if (!user.uid) throw new Error('No user id provided');
        let userDocRef = doc(DB, 'users', user.uid);
        updateDoc(userDocRef, {
          ...user,
        });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'User', id: arg.uid }],
    }),
  }),
});
// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useFetchInvitationByIdQuery,
  useFetchInvitationsByInviterIdQuery,
  useFetchUsersQuery,
  useFetchAgentsQuery,
  useFetchUserQuery,
  useFetchIdsAndPendingsQuery,
  useFetchUserByUidQuery,
  useFetchUserByEmailQuery,
  useEditUserMutation,
  useEditUserStatusMutation,
} = AdminApi;
